import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { LANG_CODES, normalizePoojaApiLang, normalizeUiLang } from '../config/languages';
import { ensureLocale } from '../i18n/translations';

export { LANGUAGES, getLanguageLabel, getLanguageShortLabel } from '../config/languages';

const LANG_STORAGE_KEY = 'daiv_lang';
const defaultLang = 'en';

function getLangFromUrl() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const raw = (params.get('lang') || '').trim().toLowerCase();
  if (!raw) return null;
  if (!LANG_CODES.includes(raw) && raw !== 'ka') return null;
  const ui = normalizeUiLang(raw);
  return LANG_CODES.includes(ui) ? ui : null;
}

function getLangFromStorage() {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (!stored) return null;
    const ui = normalizeUiLang(stored);
    return LANG_CODES.includes(ui) ? ui : null;
  } catch {
    return null;
  }
}

const LanguageContext = createContext({
  lang: defaultLang,
  setLang: () => {},
  isSwitchingLanguage: false,
});

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    return getLangFromUrl() || getLangFromStorage() || defaultLang;
  });
  const [isSwitchingLanguage, setIsSwitchingLanguage] = useState(false);

  useEffect(() => {
    const urlLang = getLangFromUrl();
    if (urlLang) {
      setLangState(urlLang);
      try {
        localStorage.setItem(LANG_STORAGE_KEY, urlLang);
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount to read ?lang= from URL
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    ensureLocale(lang).catch(() => {});
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!LANG_CODES.includes(code) || code === lang) return;
    setIsSwitchingLanguage(true);
    setLangState(code);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, code);
    } catch {}
    const startedAt = Date.now();
    const apiLang = normalizePoojaApiLang(code);
    Promise.all([
      ensureLocale(code),
      import('../utils/pooja')
        .then((m) => m.getPoojas(false, apiLang))
        .catch(() => {
          // Best effort preload; page-level loaders already handle failures.
        }),
    ]).finally(() => {
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, 450 - elapsed);
      window.setTimeout(() => setIsSwitchingLanguage(false), remaining);
    });
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, isSwitchingLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    return { lang: defaultLang, setLang: () => {}, isSwitchingLanguage: false };
  }
  return context;
}
